import React from 'react';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, AlertCircle, Clock, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

export const ConsolePreliminariesTab = ({
  preliminaries, setPreliminaries, savePreliminaries,
  users, attendance, lateArrivals, leftEarly, quorumStatus,
  additionalAgendaInput, setAdditionalAgendaInput,
}) => (
  <div className="space-y-5">
    {/* 1) Designation of Presiding Officer */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">1. Designation of Presiding Officer</Label>
      <p className="text-xs text-[#5A6570] mt-0.5 mb-1">If Chair or Vice Chair are absent, members present may designate a Presiding Officer.</p>
      <Select value={preliminaries.presiding_officer_id || ''} onValueChange={v => savePreliminaries({ presiding_officer_id: v })}>
        <SelectTrigger className="border-[#E2E6EA]" data-testid="presiding-officer-select"><SelectValue placeholder="Select Presiding Officer" /></SelectTrigger>
        <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
      </Select>
      <Textarea value={preliminaries.presiding_officer_notes || ''} onChange={e => setPreliminaries({ ...preliminaries, presiding_officer_notes: e.target.value })}
        onBlur={() => savePreliminaries({ presiding_officer_notes: preliminaries.presiding_officer_notes })}
        placeholder="Notes on designation (e.g., Chair absent, Vice Chair presided per by-laws)..." className="mt-2 border-[#E2E6EA] min-h-[50px]" data-testid="presiding-officer-notes" />
    </div>

    {/* 2) Call to Order */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">2. Call to Order</Label>
      <p className="text-xs text-[#5A6570] mt-0.5">Time is automatically recorded when "Start Meeting" is clicked.</p>
      <div className="mt-2 p-3 rounded-lg border border-[#E2E6EA] bg-[#F8F9FA]" data-testid="call-to-order-time">
        {preliminaries.call_to_order_time ? (
          <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-sm font-medium text-[#1A1D21]">Called to order at {format(new Date(preliminaries.call_to_order_time), 'h:mm a')}</span></div>
        ) : (
          <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#A8B1B9]" /><span className="text-sm text-[#5A6570]">Awaiting "Start Meeting" to record time</span></div>
        )}
      </div>
    </div>

    {/* 3) Roll Call */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">3. Roll Call</Label>
      <p className="text-xs text-[#5A6570] mt-0.5 mb-1">Use the Attendance panel on the left to record attendance. Add any notes below.</p>
      <div className="mt-1 p-3 rounded-lg border border-[#E2E6EA] bg-[#F8F9FA]">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-green-700 font-medium">{attendance.filter(a => a.status === 'present_onsite' || a.status === 'present_virtual').length} Present</span>
          <span className="text-amber-700 font-medium">{lateArrivals.length} Late</span>
          <span className="text-red-500 font-medium">{leftEarly.length} Left Early</span>
          <span className="text-gray-500">{users.length - attendance.filter(a => a.status !== 'absent').length} Absent</span>
        </div>
      </div>
      <Textarea value={preliminaries.roll_call_notes || ''} onChange={e => setPreliminaries({ ...preliminaries, roll_call_notes: e.target.value })}
        onBlur={() => savePreliminaries({ roll_call_notes: preliminaries.roll_call_notes })}
        placeholder="Additional roll call notes..." className="mt-2 border-[#E2E6EA] min-h-[40px]" data-testid="roll-call-notes" />
    </div>

    {/* 4) Quorum Verification */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">4. Quorum Verification by Secretariat</Label>
      <div className={`mt-2 p-3 rounded-lg border ${quorumStatus.achieved ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
        <div className="flex items-center gap-2">
          {quorumStatus.achieved ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-amber-600" />}
          <span className={`text-sm font-medium ${quorumStatus.achieved ? 'text-green-800' : 'text-amber-800'}`}>
            {quorumStatus.achieved ? 'Quorum Achieved' : 'No Quorum Yet'} — {quorumStatus.present} of {quorumStatus.required} required ({quorumStatus.total} total members)
          </span>
        </div>
      </div>
      <Textarea value={preliminaries.quorum_notes || ''} onChange={e => setPreliminaries({ ...preliminaries, quorum_notes: e.target.value })}
        onBlur={() => savePreliminaries({ quorum_notes: preliminaries.quorum_notes })}
        placeholder="Secretariat's quorum verification notes..." className="mt-2 border-[#E2E6EA] min-h-[40px]" data-testid="quorum-notes" />
    </div>

    {/* 5) Approval of Minutes of Previous Meeting */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">5. Approval of Minutes of Previous Meeting</Label>
      <div className="mt-2 space-y-2">
        <Select value={preliminaries.minutes_approval_type || ''} onValueChange={v => savePreliminaries({ minutes_approval_type: v })}>
          <SelectTrigger className="border-[#E2E6EA]" data-testid="minutes-approval-type"><SelectValue placeholder="Select approval action" /></SelectTrigger>
          <SelectContent><SelectItem value="approved">Motion to Approve</SelectItem><SelectItem value="deferred">Deferred Approval</SelectItem></SelectContent>
        </Select>
        {preliminaries.minutes_approval_type === 'approved' && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Motion By</Label>
              <Select value={preliminaries.minutes_motion_by || ''} onValueChange={v => savePreliminaries({ minutes_motion_by: v })}>
                <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Seconded By</Label>
              <Select value={preliminaries.minutes_seconded_by || ''} onValueChange={v => savePreliminaries({ minutes_seconded_by: v })}>
                <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        {preliminaries.minutes_approval_type === 'deferred' && (
          <div className="p-3 bg-[#FFF7ED] border border-[#F58A26]/20 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#F58A26]">Deferred for review by Board/Committee for</Label>
              <Input type="number" min="1" value={preliminaries.minutes_deferred_days || ''} onChange={e => setPreliminaries({ ...preliminaries, minutes_deferred_days: parseInt(e.target.value) || '' })}
                onBlur={() => savePreliminaries({ minutes_deferred_days: preliminaries.minutes_deferred_days })}
                className="w-20 h-7 text-sm border-[#F58A26]/30 text-center" data-testid="minutes-deferred-days" />
              <Label className="text-xs text-[#F58A26]">working days</Label>
            </div>
            <Textarea value={preliminaries.minutes_deferred_notes || ''} onChange={e => setPreliminaries({ ...preliminaries, minutes_deferred_notes: e.target.value })}
              onBlur={() => savePreliminaries({ minutes_deferred_notes: preliminaries.minutes_deferred_notes })}
              placeholder="Notes on deferred approval..." className="border-[#E2E6EA] min-h-[40px]" data-testid="minutes-deferred-notes" />
          </div>
        )}
      </div>
    </div>

    {/* 6) Approval of Agenda */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">6. Approval of Agenda</Label>
      <div className="mt-2 space-y-2">
        <Select value={preliminaries.agenda_approval_type || ''} onValueChange={v => savePreliminaries({ agenda_approval_type: v })}>
          <SelectTrigger className="border-[#E2E6EA]" data-testid="agenda-approval-type"><SelectValue placeholder="Select approval action" /></SelectTrigger>
          <SelectContent><SelectItem value="as_read">Motion to Approve as Read</SelectItem><SelectItem value="as_revised">Motion to Approve as Revised</SelectItem></SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Motion By</Label>
            <Select value={preliminaries.agenda_motion_by || ''} onValueChange={v => savePreliminaries({ agenda_motion_by: v })}>
              <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Seconded By</Label>
            <Select value={preliminaries.agenda_seconded_by || ''} onValueChange={v => savePreliminaries({ agenda_seconded_by: v })}>
              <SelectTrigger className="border-[#E2E6EA] h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {preliminaries.agenda_approval_type === 'as_revised' && (
          <div className="p-3 bg-[#F0F9FF] border border-blue-200 rounded-lg space-y-2">
            <Label className="text-xs text-blue-700 font-medium">Additional Agenda Items Approved</Label>
            {(preliminaries.additional_agenda_items || []).length > 0 && (
              <div className="space-y-1">
                {preliminaries.additional_agenda_items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="text-blue-600 font-medium">{idx + 1}.</span>
                    <span className="flex-1 text-[#1A1D21]">{item}</span>
                    <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400"
                      onClick={() => { const updated = (preliminaries.additional_agenda_items || []).filter((_, i) => i !== idx); savePreliminaries({ additional_agenda_items: updated }); }}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input value={additionalAgendaInput} onChange={e => setAdditionalAgendaInput(e.target.value)}
                placeholder="Additional agenda item..." className="border-blue-200 h-8 text-sm flex-1" data-testid="additional-agenda-input"
                onKeyDown={e => { if (e.key === 'Enter' && additionalAgendaInput.trim()) { const updated = [...(preliminaries.additional_agenda_items || []), additionalAgendaInput.trim()]; savePreliminaries({ additional_agenda_items: updated }); setAdditionalAgendaInput(''); } }} />
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"
                onClick={() => { if (additionalAgendaInput.trim()) { const updated = [...(preliminaries.additional_agenda_items || []), additionalAgendaInput.trim()]; savePreliminaries({ additional_agenda_items: updated }); setAdditionalAgendaInput(''); } }}
                data-testid="add-additional-agenda-btn"><Plus className="h-3 w-3 mr-1" />Add</Button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* 7) Matters Arising from Previous Meeting */}
    <div>
      <Label className="text-sm font-semibold text-[#1A1D21]">7. Matters Arising from Previous Meeting (Unfinished Business)</Label>
      <Textarea value={preliminaries.matters_arising || ''} onChange={e => setPreliminaries({ ...preliminaries, matters_arising: e.target.value })}
        onBlur={() => savePreliminaries({ matters_arising: preliminaries.matters_arising })}
        placeholder="Record any unfinished business or matters arising from the previous meeting..." className="mt-1 border-[#E2E6EA] min-h-[80px]" data-testid="matters-arising" />
    </div>
  </div>
);
